/* eslint-disable react-refresh/only-export-components */
import React, { useRef, useState } from 'react';
import {
    Box,
    Chip,
    CircularProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Tooltip,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Tune as TuneIcon,
    Check as CheckIcon,
    PrecisionManufacturing as BrandIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useExpertAccess } from '../expert-chat/useExpertAccess';
import { extractInvokeErrorDetails, mapExpertChatError } from '../expert-chat/useExpertChat';
import { supabase } from '../../lib/supabase';
import type { EquipmentScope } from '../help-chat/helpChat.types';

function useSafeUserId(): string | null {
    try {
        const auth = useAuth();
        return auth?.user?.id ?? null;
    } catch {
        return null;
    }
}

export interface EquipmentBrandChipProps {
    equipmentScope?: EquipmentScope | null;
    expertSessionId?: string | null;
    onEquipmentChanged?: (newScope: EquipmentScope) => void;
}

export function formatEquipmentLabel(scope?: EquipmentScope | null): string {
    if (!scope) return '';
    const brand = scope.manufacturer_display_name || (scope.manufacturer_id ? 'Selected brand' : 'All brands');
    const model = scope.model_label || '';
    if (brand === 'All brands') return 'Searching: All brands';
    return `Searching: ${[brand, model].filter(Boolean).join(' ')}`;
}

export const EquipmentBrandChip: React.FC<EquipmentBrandChipProps> = ({
    equipmentScope,
    expertSessionId,
    onEquipmentChanged,
}) => {
    const theme = useTheme();
    const access = useExpertAccess();
    const currentUserId = useSafeUserId();
    const currentUserIdRef = useRef<string | null>(currentUserId);
    currentUserIdRef.current = currentUserId;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    if (!equipmentScope) return null;

    const label = formatEquipmentLabel(equipmentScope);
    const isInteractive = Boolean(expertSessionId);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        if (!isInteractive || isUpdating) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelectManufacturer = async (mfrId: string | null, displayName: string) => {
        handleClose();
        if (!expertSessionId) return;

        const callerUserId = currentUserIdRef.current;

        setIsUpdating(true);
        setErrorMessage(null);
        try {
            const isSameManufacturer = Boolean(mfrId && equipmentScope.manufacturer_id === mfrId);
            const body: Record<string, unknown> = {
                action: 'confirm_equipment',
                session_id: expertSessionId,
                manufacturer_id: mfrId,
                error_code: equipmentScope.error_code ?? null,
                serial_number: equipmentScope.serial_number ?? null,
            };

            if (isSameManufacturer) {
                if (equipmentScope.model_id !== undefined && equipmentScope.model_id !== null) {
                    body.model_id = equipmentScope.model_id;
                } else if (equipmentScope.model_label) {
                    body.model_text = equipmentScope.model_label;
                }
                if (equipmentScope.series_id !== undefined) body.series_id = equipmentScope.series_id;
                if ((equipmentScope as Record<string, unknown>).firmware_version !== undefined) {
                    body.firmware_version = (equipmentScope as Record<string, unknown>).firmware_version;
                }
            }

            const { data, error } = await supabase.functions.invoke('expert-chat', {
                body,
            });

            if (currentUserIdRef.current !== callerUserId) {
                return;
            }

            if (error) {
                console.warn('Failed to confirm equipment:', error);
                const details = await extractInvokeErrorDetails(error, data);
                if (currentUserIdRef.current !== callerUserId) {
                    return;
                }
                const mapped = mapExpertChatError(details.rawMessage, details.code, details.status);
                const msg =
                    details.rawMessage &&
                    details.rawMessage.trim() &&
                    details.rawMessage.trim() !== 'Edge Function returned a non-2xx status code'
                        ? details.rawMessage.trim()
                        : mapped.message || 'Failed to update equipment context';
                setErrorMessage(msg);
                return;
            }

            const updatedScope: EquipmentScope = {
                manufacturer_id: mfrId,
                manufacturer_display_name: displayName,
                model_id: isSameManufacturer ? (equipmentScope.model_id ?? null) : null,
                model_label: isSameManufacturer ? (equipmentScope.model_label ?? null) : null,
                series_id: isSameManufacturer ? (equipmentScope.series_id ?? null) : null,
                serial_number: equipmentScope.serial_number ?? null,
                error_code: equipmentScope.error_code ?? null,
                source: 'CONFIRMED',
            };
            onEquipmentChanged?.(updatedScope);
        } catch (err) {
            if (currentUserIdRef.current !== callerUserId) {
                return;
            }
            console.warn('Error confirming equipment:', err);
            const details = await extractInvokeErrorDetails(err);
            if (currentUserIdRef.current !== callerUserId) {
                return;
            }
            const mapped = mapExpertChatError(details.rawMessage, details.code, details.status);
            const msg =
                details.rawMessage &&
                details.rawMessage.trim() &&
                details.rawMessage.trim() !== 'Edge Function returned a non-2xx status code'
                    ? details.rawMessage.trim()
                    : mapped.message || 'Error confirming equipment';
            setErrorMessage(msg);
        } finally {
            if (currentUserIdRef.current === callerUserId) {
                setIsUpdating(false);
            }
        }
    };

    const currentMfrId = equipmentScope.manufacturer_id ?? null;

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip
                title={
                    isInteractive
                        ? 'Tap to change equipment context or switch to All brands'
                        : 'Current equipment search context'
                }
            >
                <Chip
                    data-testid="equipment-brand-chip"
                    icon={
                        isUpdating ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : isInteractive ? (
                            <TuneIcon sx={{ fontSize: '1rem !important' }} />
                        ) : (
                            <BrandIcon sx={{ fontSize: '1rem !important' }} />
                        )
                    }
                    label={label}
                    size="small"
                    variant="outlined"
                    clickable={isInteractive && !isUpdating}
                    onClick={handleClick}
                    sx={{
                        fontSize: '0.74rem',
                        fontWeight: 600,
                        height: 26,
                        borderColor: alpha(theme.palette.secondary.main, 0.4),
                        bgcolor: alpha(theme.palette.secondary.main, 0.08),
                        color: theme.palette.secondary.dark || theme.palette.secondary.main,
                        cursor: isInteractive ? 'pointer' : 'default',
                        '&:hover': isInteractive
                            ? {
                                  bgcolor: alpha(theme.palette.secondary.main, 0.16),
                              }
                            : undefined,
                    }}
                />
            </Tooltip>

            {isInteractive && (
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            maxHeight: 320,
                            minWidth: 200,
                            boxShadow: 3,
                            borderRadius: 2,
                        },
                    }}
                >
                    <MenuItem
                        selected={currentMfrId === null}
                        onClick={() => void handleSelectManufacturer(null, 'All brands')}
                    >
                        {currentMfrId === null && (
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <CheckIcon fontSize="small" color="secondary" />
                            </ListItemIcon>
                        )}
                        <ListItemText inset={currentMfrId !== null}>
                            All brands (general &amp; standards)
                        </ListItemText>
                    </MenuItem>

                    {access.manufacturers.map((m) => {
                        const isSelected = currentMfrId === m.id;
                        return (
                            <MenuItem
                                key={m.id}
                                selected={isSelected}
                                onClick={() => void handleSelectManufacturer(m.id, m.display_name)}
                            >
                                {isSelected && (
                                    <ListItemIcon sx={{ minWidth: 28 }}>
                                        <CheckIcon fontSize="small" color="secondary" />
                                    </ListItemIcon>
                                )}
                                <ListItemText inset={!isSelected}>{m.display_name}</ListItemText>
                            </MenuItem>
                        );
                    })}
                </Menu>
            )}

            {errorMessage && (
                <Box
                    component="span"
                    role="alert"
                    sx={{
                        display: 'inline-block',
                        ml: 1,
                        fontSize: '0.72rem',
                        color: 'error.main',
                        fontWeight: 500,
                    }}
                >
                    {errorMessage}
                </Box>
            )}
        </Box>
    );
};

export default EquipmentBrandChip;
